import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pedidosApi, freteApi } from '@/lib/api';
import type { Pedido, StatusPedido } from '@/types';

interface CriarPedidoData {
  origem: string;
  clienteId: string;
  tipoPagamento: string;
  items: Array<{
    produtoId: string;
    quantidade: number;
    precoUnitario: number;
    custoUnitarioReal?: number;
  }>;
  freteValor: number;
  freteModal: string;
  cupomCodigo?: string;
}

export function usePedidos(params?: {
  status?: string;
  origem?: string;
  clienteId?: string;
  dataInicio?: string;
  dataFim?: string;
}) {
  return useQuery({
    queryKey: ['pedidos', params],
    queryFn: async () => {
      const response = await pedidosApi.listar(params);
      const data = response.data;
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        return data.data as Pedido[];
      }
      return (Array.isArray(data) ? data : []) as Pedido[];
    },
  });
}

export function usePedido(id: string) {
  return useQuery({
    queryKey: ['pedido', id],
    queryFn: async () => {
      const response = await pedidosApi.buscar(id);
      return (response.data.data || response.data) as Pedido;
    },
    enabled: !!id,
  });
}

export function useCriarPedido() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CriarPedidoData) => pedidosApi.criar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    },
  });
}

export function useAtualizarStatusPedido() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: StatusPedido }) =>
      pedidosApi.atualizarStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['pedido', id] });
    },
  });
}

export function useGerarEtiqueta() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (pedidoId: string) => freteApi.gerarEtiqueta(pedidoId),
    onSuccess: (_, pedidoId) => {
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['pedido', pedidoId] });
    },
  });
}

export function useDashboardPedidos() {
  return useQuery({
    queryKey: ['pedidos-dashboard'],
    queryFn: async () => {
      const response = await pedidosApi.dashboardStats();
      return response.data.data || response.data;
    },
  });
}
